import {
  queryOptions,
  skipToken,
  useQuery,
  type QueryFunctionContext,
  type UseQueryOptions,
} from '@tanstack/react-query';

import { type UUID } from '~/types/common';

import { queryKeysDeliveryNote } from '../queryKeys';

import { fetchContractSuggestions } from './fetchContractSuggestions';
import { type ContractSuggestion } from './types';

export { type ContractSuggestion } from './types';

/**
 * Generates the query options for the contract suggestions query so they can be
 * shared between the useQuery hook and prefetching.
 *
 * @param assetId - The delivery note asset ID.
 * @param options - Additional options for the query.
 * @see https://app.dev.vestigas.com/redoc#tag/Delivery-Note/operation/get_contract_suggestions_delivery_note__asset_id__contract_suggestions_get
 */
export const getDeliveryNoteContractSuggestionsQueryOptions = <
  TData = ContractSuggestion[],
>({
  assetId,
  options,
}: {
  assetId: UUID;
  options?: Omit<
    UseQueryOptions<ContractSuggestion[], Error, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    queryFn: assetId
      ? async ({ signal }: QueryFunctionContext) =>
          fetchContractSuggestions(assetId, { signal })
      : skipToken,
    queryKey: queryKeysDeliveryNote.getContractSuggestions(assetId),
    ...options,
  });

/**
 * React Query hook for fetching matching contract suggestions for a delivery note.
 *
 * Results are ranked by match strength (lower is stronger). Rank 0 is a synthetic
 * rank for the currently linked contract when it no longer matches; ranks 1–3 are
 * order-reference and accounting-reference matches. Additional ranks may appear.
 * When `isCurrent` is true, prefer that flag over `matchRank`.
 *
 * @param assetId - The delivery note asset ID.
 * @param options - Additional options for the useQuery hook.
 * @see https://app.dev.vestigas.com/redoc#tag/Delivery-Note/operation/get_contract_suggestions_delivery_note__asset_id__contract_suggestions_get
 */
export const useQueryDeliveryNoteContractSuggestions = <
  TData = ContractSuggestion[],
>(
  assetId: UUID | undefined,
  options?: Omit<
    UseQueryOptions<ContractSuggestion[], Error, TData>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery({
    ...getDeliveryNoteContractSuggestionsQueryOptions<TData>({
      assetId: assetId!,
      options,
    }),
    enabled: Boolean(assetId) && (options?.enabled ?? true),
  });
